import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { formatAIChatTime } from '../../components/Succession/Messaging/shared/dateUtils';
import Modal from '../../../../ui/canon/Modal.jsx';
import Button from '../../../../ui/canon/Button.jsx';
import classNames from 'classnames';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { XMarkIcon, ArrowPathIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

// AssistantModalV2
// Full-screen modal dialog for AI assistant (pixel-aligned with AssistantChat tokens)
export default function AssistantModalV2({
  open,
  onOpenChange,
  initialMessage = '',
  suggestedTopics = [],
  contextualPrompts = [],
  onSendMessage,
  companyBrandedName,
}) {
  const intl = useIntl();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const scrollRef = useRef(null);
  const forceScrollRef = useRef(false);
  const pinnedRef = useRef(true);

  const scrollToBottom = (smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  // Auto-send initial message when opening with initialMessage
  useEffect(() => {
    if (open && initialMessage && messages.length === 0) {
      handleSend(initialMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMessage]);

  // Maintain pinned scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    if (atBottom || forceScrollRef.current || pinnedRef.current) {
      scrollToBottom(!atBottom);
      forceScrollRef.current = false;
    }
  }, [messages, isThinking]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      pinnedRef.current = atBottom;
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const isEmpty = messages.length === 0 && !isThinking;

  const handleSend = (messageContent) => {
    const text = (messageContent != null ? messageContent : input).trim();
    if (!text) return;

    const userMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: 'Just now',
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsThinking(true);

    if (onSendMessage) onSendMessage(text);

    // Simulated assistant response (swap later for agent call)
    setTimeout(() => {
      const assistantMessage = {
        id: `msg-${Date.now()}-assistant`,
        role: 'assistant',
        content:
          "This is a simulated response. In production, this would be generated by your AI assistant based on the user's question and context from your practice valuation data.",
        timestamp: 'Just now',
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsThinking(false);
    }, 900);

    forceScrollRef.current = true;
    pinnedRef.current = true;
    setTimeout(() => scrollToBottom(true), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      onOpenChange?.(false);
    }
  };

  const brand = companyBrandedName || (typeof document !== 'undefined' && document.store?.getState?.()?.company?.branded_name) || 'Continuum';

  return (
    <Modal
      open={!!open}
      onClose={() => onOpenChange?.(false)}
      overlayClassName="fixed inset-0 z-50 bg-black/40"
      panelClassName="absolute inset-0 flex items-center justify-center p-4"
      className
    >
      <div className="bg-succession-bg-primary text-succession-text-primary rounded-xl shadow-succession-lg border border-succession-border w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-succession-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-succession-secondary/10 flex items-center justify-center">
              <SparklesIcon className="h-5 w-5 text-succession-secondary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold m-0 p-0">{intl.formatMessage({ id: 'assistant.modal.header_title' }, { brand })}</h2>
              <p className="text-sm text-succession-text-secondary m-0 p-0">{intl.formatMessage({ id: 'assistant.modal.header_subtitle' })}</p>
            </div>
          </div>
          <button
            onClick={() => onOpenChange?.(false)}
            className="flex-shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-succession-bg-secondary text-succession-text-secondary"
            aria-label={intl.formatMessage({ id: 'assistant.modal.close' })}
            title={intl.formatMessage({ id: 'assistant.modal.close' })}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
          {isEmpty ? (
            <div className="max-w-3xl mx-auto text-center space-y-8 py-8">
              <div className="space-y-3">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-succession-secondary/10">
                  <SparklesIcon className="h-8 w-8 text-succession-secondary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold m-0 p-0">{intl.formatMessage({ id: 'assistant.modal.empty_heading' })}</h3>
                <p className="text-succession-text-secondary text-sm md:text-base max-w-prose mx-auto">
                  {intl.formatMessage({ id: 'assistant.modal.empty_subtitle' })}
                </p>
              </div>

              {Array.isArray(suggestedTopics) && suggestedTopics.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto">
                  {suggestedTopics.map((topic) => {
                    const translatedTitle = topic.id
                      ? intl.formatMessage({ id: `assistant.topics.${topic.id}.title`, defaultMessage: topic.title })
                      : topic.title;
                    const translatedDescription = topic.id && topic.description
                      ? intl.formatMessage({ id: `assistant.topics.${topic.id}.description`, defaultMessage: topic.description })
                      : topic.description;
                    return (
                      <button
                        key={topic.id || topic.title}
                        onClick={() => handleSend(translatedTitle)}
                        className={classNames(
                          'p-4 text-left rounded-lg border border-succession-border',
                          'bg-succession-bg-primary hover:bg-succession-bg-secondary',
                          'transition-all duration-200 hover:shadow-succession-lg',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring'
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div className="h-5 w-5 rounded bg-succession-secondary/10 flex items-center justify-center mt-0.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-succession-secondary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-succession-text-primary m-0 p-0">{translatedTitle}</p>
                            {translatedDescription ? (
                              <p className="text-xs text-succession-text-secondary mt-1">{translatedDescription}</p>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {Array.isArray(contextualPrompts) && contextualPrompts.length > 0 && (!suggestedTopics || suggestedTopics.length === 0) ? (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-succession-text-secondary uppercase tracking-wide">{intl.formatMessage({ id: 'assistant.modal.suggested_questions' })}</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {contextualPrompts.map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSend(prompt)}
                        className="px-3 py-1.5 rounded-md border border-succession-border text-sm bg-succession-bg-primary hover:bg-succession-bg-secondary"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((m) => (
                <div key={m.id} className={classNames('flex gap-4', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                  {m.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary/10 flex items-center justify-center">
                      <SparklesIcon className="h-4 w-4 text-succession-secondary" />
                    </div>
                  )}
                  <div className={classNames('rounded-lg p-4 max-w-[80%]', m.role === 'user' ? 'bg-succession-secondary text-white' : 'bg-succession-bg-primary border border-succession-border')}>
                    <p className={classNames('text-sm whitespace-pre-wrap m-0 p-0', m.role === 'user' ? 'text-white' : 'text-succession-text-primary')}>{m.content}</p>
                    <p className={classNames('text-xs mt-2', m.role === 'user' ? 'text-white/70' : 'text-succession-text-secondary')}>
                      {(() => {
                        const formatted = formatAIChatTime(m.timestamp);
                        return formatted === 'Invalid date' ? m.timestamp : formatted;
                      })()}
                    </p>
                  </div>
                  {m.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-bg-secondary flex items-center justify-center">
                      <span className="text-xs font-medium text-succession-text-primary">{intl.formatMessage({ id: 'assistant.modal.you_label' })}</span>
                    </div>
                  )}
                </div>
              ))}

              {isThinking && (
                <div className="flex gap-4 justify-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary/10 flex items-center justify-center">
                    <SparklesIcon className="h-4 w-4 text-succession-secondary" />
                  </div>
                  <div className="rounded-lg p-4 bg-succession-bg-primary border border-succession-border">
                    <div className="flex items-center gap-2">
                      <ArrowPathIcon className="h-4 w-4 text-succession-secondary animate-spin" />
                      <p className="text-sm text-succession-text-secondary m-0 p-0">{intl.formatMessage({ id: 'assistant.modal.thinking' })}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-succession-border bg-succession-bg-primary px-6 py-4 flex-shrink-0">
          <div className="max-w-3xl mx-auto">
            <div className="relative flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={intl.formatMessage({ id: 'assistant.modal.input_placeholder' }, { brand })}
                rows={1}
                className="flex-1 min-h-[60px] max-h-[200px] resize-none rounded-md bg-succession-bg-secondary border border-succession-border text-succession-text-primary placeholder-succession-text-muted px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring"
              />
              <Button onClick={() => handleSend()} disabled={!input.trim() || isThinking} size="sm" className="h-10 w-10 p-0 rounded-full">
                <PaperAirplaneIcon className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-xs text-succession-text-secondary text-center mt-2">{intl.formatMessage({ id: 'assistant.modal.disclaimer' }, { brand })}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}


